import type { BeckyFunctionLibrary, BeckyRequestPayload, OoiCssInstanceModel } from '@wix/thunderbolt-becky-root'
import { ModuleFetcher } from '@wix/thunderbolt-becky-types'
import { decodeModulesToLoad } from '@wix/thunderbolt-commons'
import moduleResolverFactory from './moduleResolverFactory'
import { variantSeparator } from '../../carmi/variants/constants'

const resolveWidgetsToLoad = (payload: BeckyRequestPayload, widgetIdsOnPage: Array<string> = []) => {
	const allWidgetsOnSite = decodeModulesToLoad(payload.ooiVersions || '')
	return allWidgetsOnSite.filter((w) => widgetIdsOnPage.find((widgetId) => widgetId === w.id))
}

const staticCssProcessorMemoizer = (getBuildTimeStaticCss: (cssConfig: object) => { css: string; hash?: string }) => {
	const staticCssHashes: { [hash: string]: boolean } = {}
	return (cssConfig: object) => {
		const { css, hash } = getBuildTimeStaticCss(cssConfig)
		if (!hash) {
			// backward-compatibility: cssConfig might not include the new build-time hashing feature (in tpa-webpack-plugin)
			return css
		}
		if (staticCssHashes[hash]) {
			// since staticCss already exists, we don't to return it
			return ''
		}
		staticCssHashes[hash] = true
		return css
	}
}

function processOoiCssFactory({
	resolveModule,
	options,
	runAndReport,
	enableOoiCssVars,
}: {
	resolveModule: (id: string) => any
	options: OoiCssInstanceModel
	runAndReport: BeckyFunctionLibrary['runAndReport']
	enableOoiCssVars: boolean
}) {
	// can't use typescript import due to jest transfer exploding when looking into tpa-style-webpack-plugin (and es6 module)
	const {
		getProcessedCssWithConfig,
		getBuildTimeStaticCss,
		getProcessedCssWithVars,
	} = require('@wix/tpa-style-webpack-plugin/standalone')
	const staticCssProcessor = staticCssProcessorMemoizer(getBuildTimeStaticCss)

	return (
		widgetId: string,
		styleData: any,
		{ prefixSelector, withStaticCss = true }: { prefixSelector: string; withStaticCss?: boolean }
	): string => {
		const { cssConfig, processCssVars } = resolveModule(widgetId) || {}
		const [originalPrefixSelector] = prefixSelector.split(variantSeparator)
		const { siteColors, siteTextPresets, style: styleParams } = styleData

		if (enableOoiCssVars) {
			const {
				getSiteCssVariables,
				getComponentCssVariables,
			} = require('@wix/tpa-style-webpack-plugin-css-vars/standalone')

			const siteCssVars = getSiteCssVariables(siteColors, siteTextPresets)
			const widgetCssVars = getComponentCssVariables(
				{ siteColors, siteTextPresets, styleParams },
				{},
				{},
				{ prefixSelector: originalPrefixSelector },
				{}
			)
			return `${originalPrefixSelector}{${widgetCssVars.stylesheet}${siteCssVars.stylesheet}}`
		}

		if (!processCssVars) {
			if (!cssConfig) {
				return ''
			}
			// cssConfig
			const cssConfigCss = runAndReport(
				() =>
					getProcessedCssWithConfig(
						cssConfig,
						{
							siteColors,
							siteTextPresets,
							styleParams,
						},
						{
							...options,
							dimensions: options.dimensions[widgetId],
							prefixSelector: originalPrefixSelector,
						}
					),
				`getProcessedCssWithConfig-${widgetId}`
			)

			const staticCss = withStaticCss
				? runAndReport(() => staticCssProcessor(cssConfig), `staticCssProcessor-${widgetId}`)
				: ''
			return staticCss + cssConfigCss
		} else {
			// else: css vars. Call the runtime file (which is replacing the '.cssConfig.' file)
			const defaults = {}
			// Css vars dynamic values, built during build time
			const { cssVarsExpressions, dynamicExpressions } = processCssVars
			const cssVarsRuntimeOutput = getProcessedCssWithVars(
				dynamicExpressions,
				cssVarsExpressions,
				{ siteColors, siteTextPresets, styleParams },
				{
					...options,
					dimensions: options.dimensions[widgetId],
					prefixSelector: originalPrefixSelector,
				},
				defaults
			)

			const cssVars = Object.keys(cssVarsRuntimeOutput).reduce((acc, key) => {
				return `${acc}--${key}:${cssVarsRuntimeOutput[key]};`
			}, '')
			return `${originalPrefixSelector}{${cssVars}}`
		}
	}
}

export const processOoiCssImpl = async (
	{ widegtsIds, ...options }: { widegtsIds?: Array<string> } & OoiCssInstanceModel,
	{
		funcLib: { runAsyncAndReport, runAndReport },
		moduleFetcher,
		payload,
	}: {
		payload: BeckyRequestPayload
		funcLib: BeckyFunctionLibrary
		moduleFetcher: ModuleFetcher
	}
) => {
	const widgetsToLoad = resolveWidgetsToLoad(payload, widegtsIds)
	const resolveModule = await moduleResolverFactory(widgetsToLoad, {
		moduleFetcher,
		runAsyncAndReport,
	})

	return processOoiCssFactory({
		resolveModule,
		options: options as OoiCssInstanceModel,
		runAndReport,
		enableOoiCssVars: payload.enableOoiCssVars === 'true',
	})
}
